import { HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { map, Observable } from 'rxjs';
import { API_CONFIG, ApiCallingV2Service } from '@zayan-erp/shared';
import {
  SystemActivityLog,
  SystemActivityLogPage,
  SystemActivityLogQuery,
  SystemActivityType
} from '../models/system-activity-log.models';

@Injectable({
  providedIn: 'root'
})
export class SystemActivityLogsService {
  readonly #http = inject(ApiCallingV2Service);
  readonly #baseUrl = `${inject(API_CONFIG).baseUrlV2}/system-activity`;

  /**
   * The endpoint answers with a bare array — no total count, no pagination headers — so one
   * row past the page is requested to learn whether a next page exists, then dropped.
   * `IsValidDateRange` is not sent: it is a read-only property the backend computes itself
   * and rejects the request over, so the range is kept valid on this side instead.
   */
  getSystemActivityLogs(query: SystemActivityLogQuery): Observable<SystemActivityLogPage> {
    let params = new HttpParams()
      .set('PageNumber', query.pageNumber)
      .set('PageSize', query.pageSize + 1)
      .set('SortBy', query.sortBy)
      .set('SortOrder', query.sortOrder);

    if (query.activityType) {
      params = params.set('ActivityType', query.activityType);
    }
    if (query.fromDate) {
      params = params.set('FromDate', query.fromDate);
    }
    if (query.toDate) {
      params = params.set('ToDate', query.toDate);
    }

    return this.#http
      .getData<SystemActivityLog[]>(this.#baseUrl, {
        showLoader: false,
        showMessage: false,
        options: { params }
      })
      .pipe(
        map((response: SystemActivityLog[] | null) => {
          const logs = response ?? [];

          return {
            logs: logs.slice(0, query.pageSize),
            hasNextPage: logs.length > query.pageSize
          };
        })
      );
  }

  getActivityTypes(): Observable<SystemActivityType[]> {
    return this.#http
      .getData<SystemActivityType[]>(`${this.#baseUrl}/activity-types`, {
        showLoader: false,
        showMessage: false
      })
      .pipe(map((response: SystemActivityType[] | null) => response ?? []));
  }
}
